import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios'
import { mechtaIDAuthService } from 'src/adapters/auth/mechta-id.service'
import { ROUTES } from 'src/presentation/shared/routes'

interface RequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

/**
 * Терминальный 401: свежий токен сервер тоже не принял (нет доступа к
 * приложению) либо сессию не обновить. Чистим сессию и уводим на `AuthPage` с
 * понятной причиной — без повторного входа, иначе при живой SSO-сессии это
 * бесконечный редирект. Роутер импортируем динамически (как в `main.ts`).
 */
async function rejectToAuthScreen() {
  await mechtaIDAuthService.endSession('access_denied')
  const { default: router } = await import('src/app/router')
  if (router.currentRoute.value.name !== ROUTES.AUTH)
    await router.push({ name: ROUTES.AUTH }).catch(() => {})
}

export function createRejectHandler(instance: AxiosInstance) {
  return async function rejectHandler(error: AxiosError) {
    if (error.response?.status !== 401) return Promise.reject(error)

    const originalRequest = error.config as RequestConfig
    const requestToken = originalRequest.headers.authorization?.replace(
      'Bearer ',
      '',
    )

    // Одна попытка восстановления на запрос: принудительный refresh + повтор.
    if (!originalRequest._retry) {
      originalRequest._retry = true

      try {
        const accessToken = await mechtaIDAuthService.refreshAccessToken()
        if (accessToken && accessToken !== requestToken) {
          originalRequest.headers.authorization = `Bearer ${accessToken}`
          return instance(originalRequest)
        }
      } catch {
        // refresh не удался — уходим на экран ниже
      }
    }

    await rejectToAuthScreen()
    return Promise.reject(error)
  }
}

export async function requestInterceptor(config: InternalAxiosRequestConfig) {
  const token = await mechtaIDAuthService.getAccessToken()

  if (token) {
    config.headers.authorization = `Bearer ${token}`
  }

  return config
}
